import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { api, ApiError } from "@/lib/api";
import { useAuthToken } from "@/lib/auth";
import { onDashboardChanged } from "@/lib/dashboardWs";
import { runnerReadiness, type RunnerReadiness } from "@/lib/runnerReadiness";
import { WarmBucketList } from "@/components/fleet/WarmBucketList";
import { RunnerBuild } from "@/components/fleet/RunnerBuild";
import { MacosHostPanel } from "@/components/fleet/MacosHostPanel";
import {
  macosBannerLines,
  reasonText,
  type MacosBoxInfo,
} from "@/lib/macosHosts";

/**
 * Fleet — Boxes view (fleet-health-dashboard-plan.md §4.7, P0 slice).
 * One card per box, worst-first, each listing every entity the hub has
 * derived a health state for (runner, identity, systemd units, publish,
 * NATS cluster) plus the platform-level capabilities (§4.8). The two
 * incidents this page exists for read as:
 *   `pure-runner-1 DEGRADED · PREWARM_HOST_BINARY_MISSING · the box is missing unzip`
 *   `pure-runner-1 DOWN · deploy-agent silent for 3m`
 *
 * Green here is NOT liveness (§4.3): a box's entities come from the
 * services' own status frames, and a silent deploy-agent makes the whole
 * box `down` regardless of what its (stale) entities last said.
 *
 * Mirrors fleetHealth.ts's FleetBox / FleetCapability exactly.
 */
type HealthState = "up" | "degraded" | "down" | "unknown";
interface FleetEntity {
  entity: string;
  state: HealthState;
  since: string | null;
  detail: unknown;
}
interface FleetBox {
  box_id: string;
  hostname: string;
  spiffe_id: string | null;
  approval_status: string | null;
  connected: boolean;
  last_seen_at: string | null;
  last_seen_ago_s: number | null;
  clock_skew_ms: number | null;
  installed_components: Record<string, string>;
  state: HealthState;
  entities: FleetEntity[];
  telemetry: Record<string, unknown>;
  /** From the SPIFFE id prefix (private/auth boxKind.ts): linux deploy-agent or the macOS host. */
  kind?: "linux" | "macos" | null;
  /** macOS host only: heartbeat status, drain flag, LAN addresses (fleetHealth.ts FleetBox.macos). */
  macos?: MacosBoxInfo | null;
}
interface FleetCapability {
  name: string;
  state: HealthState;
  reason: string;
}
interface Overview {
  ok: true;
  generated_at: string;
  boxes: FleetBox[];
  capabilities: FleetCapability[];
  incidents: number;
}

const STATE_VARIANT: Record<
  HealthState,
  "default" | "secondary" | "destructive" | "success" | "warning"
> = {
  up: "success",
  degraded: "warning",
  down: "destructive",
  unknown: "secondary",
};

const STATE_DOT: Record<HealthState, string> = {
  up: "bg-green-600",
  degraded: "bg-yellow-500",
  down: "bg-destructive",
  unknown: "bg-muted-foreground",
};

function errMsg(err: unknown, fallback: string): string {
  return err instanceof ApiError ? err.message : fallback;
}

function fmtAgo(s: number | null): string {
  if (s === null) return "never";
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.round(s / 60)}m ago`;
  return `${Math.round(s / 3600)}h ago`;
}

function fmtSince(iso: string | null): string {
  if (!iso) return "";
  const t = Date.parse(iso.replace(" ", "T") + (iso.endsWith("Z") ? "" : "Z"));
  if (!Number.isFinite(t)) return "";
  const s = Math.max(0, Math.round((Date.now() - t) / 1000));
  return `for ${fmtAgo(s).replace(" ago", "")}`;
}

/** Human label + ordering for an entity id like `runner:pure-runner-1`. */
function entityLabel(entity: string): {
  group: string;
  label: string;
  order: number;
} {
  const [kind, ...rest] = entity.split(":");
  const rem = rest.join(":");
  switch (kind) {
    case "box":
      return { group: "box", label: "deploy-agent heartbeat", order: 0 };
    case "runner":
      return { group: "runner", label: `runner ${rem}`, order: 1 };
    case "publish":
      return { group: "publish", label: `publish ${rem}`, order: 1 };
    case "macos-tester":
      return { group: "macos", label: `macOS host ${rem}`, order: 1 };
    case "identity":
      return { group: "identity", label: `identity ${rem}`, order: 2 };
    case "nats-cluster":
      return { group: "queue", label: `NATS cluster ${rem}`, order: 3 };
    case "identity-probe":
      return {
        group: "identity",
        label: "workload API probe (spire-agent)",
        order: 2,
      };
    case "cert":
      return { group: "certs", label: `cert ${rem}`, order: 3 };
    case "timer":
      return { group: "timers", label: `timer ${rem}`, order: 6 };
    case "unit":
      return { group: "systemd", label: rem, order: 5 };
    case "worker":
      return { group: "edge", label: `Worker ${rem}`, order: 1 };
    case "nats-reach":
      return {
        group: "queue",
        label: "NATS reachable from this box",
        order: 3,
      };
    default:
      return { group: kind ?? "other", label: entity, order: 9 };
  }
}

/** One package's version line — `name vX.Y.Z` normally, `name vX.Y.Z →
 *  prewarming…` while a rollout is actively fetching/rebuilding (direct
 *  operator request, 2026-09-07: visible on the Fleet page without needing
 *  box access). Returns null when there's nothing installed and nothing
 *  in flight yet (startup, before the first prewarm completes). */
function packageVersionLine(
  name: string,
  pkg: Record<string, unknown> | undefined,
): string | null {
  if (!pkg) return null;
  const installed = pkg["installed"] === true;
  const version = typeof pkg["version"] === "string" ? pkg["version"] : null;
  const prewarming = pkg["prewarming"] === true;
  if (!installed && !prewarming) return null;
  const versionPart = version ? `v${version}` : "(none yet)";
  return prewarming
    ? `${name} ${versionPart} → prewarming…`
    : `${name} ${versionPart}`;
}

/** "processing job <id> · " / "prewarming" / "ready for jobs" — direct
 *  operator request, 2026-09-21: the Fleet page could already tell a
 *  runner was unhealthy but not whether a HEALTHY one was ready for the
 *  next job, warming up, or mid-compile. `occupancy`/`job` come straight
 *  from the runner's own status payload (main.ts's buildStatusPayload),
 *  passed through fleetHealth.ts's deriveStates unmodified — empty string
 *  (no claim at all) for an older runner build that hasn't started
 *  reporting these fields yet, never defaulted to "ready". */
/** "musl warm, glibc prewarming, macos-arm64 pending" — per-bucket detail
 *  appended to the plain "prewarming" line (direct operator request,
 *  2026-09-21: "how can we better show the prewarm progress... as each
 *  one prewarms" — a single word couldn't say which of the 3
 *  WARM_TARGET_BUCKETS, or how far along). Empty string when the runner
 *  hasn't reported per-bucket detail yet (older build, or genuinely no
 *  buckets configured). */
function warmTargetsSummary(d: Record<string, unknown>): string {
  const buckets = d["warmTargets"];
  if (!Array.isArray(buckets) || buckets.length === 0) return "";
  return buckets
    .map(
      (b) => `${String(b?.["label"] ?? "?")} ${String(b?.["status"] ?? "?")}`,
    )
    .join(", ");
}

/** "warmup: disabled" when the runner runs with PURE_RUNNER_WARMUP=off
 *  (builds are cold, no cargo-target prewarm), "warmup: auto" when it
 *  prewarms in idle time with jobs first — empty for warm-up on or an
 *  older runner build that does not report the field. */
function warmupBadge(d: Record<string, unknown>): string {
  const w = d["warmup"] as
    { enabled?: unknown; mode?: unknown } | null | undefined;
  if (w && w.enabled === false) return "warmup: disabled";
  return w && w.mode === "auto" ? "warmup: auto" : "";
}

function occupancyLine(d: Record<string, unknown>): string {
  // Job admission is the primary readiness signal when the runner reports
  // it (lib/runnerReadiness.ts) — "accepting jobs" only while serving.
  const readiness = runnerReadiness(d);
  if (readiness) return readiness.line;
  const occupancy = d["occupancy"];
  if (occupancy === "processing") {
    const job = (d["job"] ?? null) as Record<string, unknown> | null;
    const jobId = typeof job?.["jobId"] === "string" ? job["jobId"] : null;
    return jobId ? `processing job ${jobId.slice(0, 8)}…` : "processing a job";
  }
  if (occupancy === "prewarming") {
    const buckets = warmTargetsSummary(d);
    return buckets ? `prewarming — ${buckets}` : "prewarming";
  }
  if (occupancy === "ready") return "ready for jobs";
  return "";
}

/** The root-cause line for an entity, straight from its transition detail
 *  — everything EXCEPT the occupancy prefix entityDetailLine adds for
 *  runner entities below (kept separate so occupancy can be prepended
 *  without disturbing this function's existing reason/error/version
 *  precedence for every other entity kind). */
function entityDetailRest(d: Record<string, unknown>): string | null {
  if (typeof d["reason"] === "string") return d["reason"];
  if (Array.isArray(d["reasons"]) && d["reasons"].length > 0)
    // reasonText: the macOS host reports machine-readable codes (on-battery,
    // clock-skew ...); any other entity's free-text reason passes through.
    return (d["reasons"] as string[]).map(reasonText).join(" · ");
  const compiler = d["compiler"] as Record<string, unknown> | undefined;
  const err = compiler?.["error"] as Record<string, unknown> | undefined;
  if (err)
    return `${String(err["code"] ?? "")} · ${String(err["hint"] ?? err["summary"] ?? "")}`;
  const interp = d["interp"] as Record<string, unknown> | undefined;
  const versionLines = [
    packageVersionLine("compiler", compiler),
    packageVersionLine("interp", interp),
  ].filter((s): s is string => s !== null);
  if (versionLines.length > 0) return versionLines.join(" · ");
  if (typeof d["systemd"] === "string") return `systemd: ${d["systemd"]}`;
  if (typeof d["status"] === "string") return `status: ${d["status"]}`;
  return null;
}

/** The root-cause line for an entity, straight from its transition detail. */
function entityDetailLine(e: FleetEntity): string | null {
  const d = (e.detail ?? null) as Record<string, unknown> | null;
  if (!d) return null;
  const isRunner = e.entity.startsWith("runner:");
  const occ = isRunner
    ? [occupancyLine(d), warmupBadge(d)].filter(Boolean).join(" · ")
    : "";
  let rest = entityDetailRest(d);
  // With admission reported, the bare `status: starting` fallback only
  // repeats what the readiness line already says.
  if (
    isRunner &&
    runnerReadiness(d) &&
    typeof d["status"] === "string" &&
    rest === `status: ${d["status"]}`
  )
    rest = null;
  if (occ && rest) return `${occ} · ${rest}`;
  return occ || rest;
}

/** Admission readiness for a runner entity whose detail is live (up /
 *  degraded) — null for other entities, stale/silent ones (their detail is
 *  the staleness reason) and older runner builds without `admission`. */
function entityReadiness(e: FleetEntity): RunnerReadiness | null {
  if (!e.entity.startsWith("runner:")) return null;
  if (e.state !== "up" && e.state !== "degraded") return null;
  return runnerReadiness((e.detail ?? null) as Record<string, unknown> | null);
}

const READINESS_DOT: Record<RunnerReadiness["badgeVariant"], string> = {
  success: "bg-green-600",
  info: "bg-blue-500",
  warning: "bg-yellow-500",
};

type Window = "24h" | "7d" | "30d";
const WINDOW_MS: Record<Window, number> = {
  "24h": 24 * 3600_000,
  "7d": 7 * 24 * 3600_000,
  "30d": 30 * 24 * 3600_000,
};

interface Transition {
  id: number;
  box_id: string;
  entity: string;
  from_state: HealthState | null;
  to_state: HealthState;
  detail: string | null;
  at: string;
}

function parseSqliteTs(s: string): number {
  return Date.parse(s.replace(" ", "T") + (s.endsWith("Z") ? "" : "Z"));
}

/**
 * Uptime strip (fleet-health-dashboard-plan.md §4.2/§4.7): the entity's
 * state over the selected window, as coloured segments, derived EXACTLY
 * from health_transitions — not sampled. The segment before the first
 * transition inside the window is that transition's from_state (what it
 * was when the window opened); after the last one, the current state.
 * Uptime % = time in `up` / window.
 */
function UptimeStrip({
  transitions,
  current,
  windowMs,
  nowMs,
}: {
  transitions: Transition[];
  current: HealthState;
  windowMs: number;
  nowMs: number;
}) {
  const start = nowMs - windowMs;
  const inWindow = transitions
    .filter((t) => parseSqliteTs(t.at) >= start)
    .sort((a, b) => parseSqliteTs(a.at) - parseSqliteTs(b.at) || a.id - b.id);
  const segments: Array<{ state: HealthState; from: number; to: number }> = [];
  let cursor = start;
  let state: HealthState = inWindow[0]?.from_state ?? current;
  for (const t of inWindow) {
    const at = Math.max(start, parseSqliteTs(t.at));
    if (at > cursor) segments.push({ state, from: cursor, to: at });
    cursor = at;
    state = t.to_state;
  }
  if (nowMs > cursor) segments.push({ state, from: cursor, to: nowMs });
  const upMs = segments
    .filter((s) => s.state === "up")
    .reduce((n, s) => n + (s.to - s.from), 0);
  const pct = windowMs > 0 ? (upMs / windowMs) * 100 : 0;
  return (
    <div className="flex items-center gap-2 mt-1">
      <div
        className="flex h-1.5 flex-1 overflow-hidden rounded-full bg-muted"
        title={`${pct.toFixed(2)}% up`}
      >
        {segments.map((s, i) => (
          <div
            key={i}
            className={cn("h-full", STATE_DOT[s.state])}
            style={{ width: `${((s.to - s.from) / windowMs) * 100}%` }}
            title={`${s.state} · ${new Date(s.from).toLocaleString()} → ${new Date(s.to).toLocaleString()}`}
          />
        ))}
      </div>
      <span className="w-14 shrink-0 text-right tabular-nums text-muted-foreground">
        {pct.toFixed(pct >= 99.95 ? 2 : 1)}%
      </span>
    </div>
  );
}

export function FleetPage() {
  const token = useAuthToken();
  const [searchParams, setSearchParams] = useSearchParams();
  const filter =
    searchParams.get("filter") === "incidents" ? "incidents" : "all";
  const [data, setData] = useState<Overview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const call = useCallback(
    <T,>(path: string, init?: RequestInit) => api<T>(token, path, init),
    [token],
  );

  const load = useCallback(() => {
    setLoading(true);
    call<Overview>("/fleet/overview")
      .then((r) => {
        setData(r);
        setError(null);
      })
      .catch((err) => setError(errMsg(err, "failed to load fleet overview")))
      .finally(() => setLoading(false));
  }, [call]);

  // Uptime window (§4.7) — deep-linked via ?window=; transitions fetched
  // once per window change + on every refresh, since the strip must
  // reflect the same moment as the current states.
  const windowKey: Window = (["24h", "7d", "30d"] as Window[]).includes(
    searchParams.get("window") as Window,
  )
    ? (searchParams.get("window") as Window)
    : "24h";
  const [transitions, setTransitions] = useState<Transition[]>([]);
  const loadTimeline = useCallback(() => {
    const since = new Date(Date.now() - WINDOW_MS[windowKey])
      .toISOString()
      .replace("T", " ")
      .replace(/\.\d+Z$/, "");
    call<{ ok: true; transitions: Transition[] }>(
      `/fleet/timeline?since=${encodeURIComponent(since)}&limit=2000`,
    )
      .then((r) => setTransitions(r.transitions))
      .catch(() => {
        /* strips just stay empty — the live state above is unaffected */
      });
  }, [call, windowKey]);
  useEffect(loadTimeline, [loadTimeline]);
  const setWindow = (w: Window) => {
    const next = new URLSearchParams(searchParams);
    if (w === "24h") next.delete("window");
    else next.set("window", w);
    setSearchParams(next, { replace: true });
  };
  const nowMs = Date.now();

  useEffect(load, [load]);
  // Live: the hub pokes the dashboard on every health transition
  // (agentHub.ts) and every sweep that changed something; 15 s poll as
  // the fallback for a missed frame.
  useEffect(() => onDashboardChanged(token, load), [token, load]);
  useEffect(() => {
    const t = setInterval(load, 15_000);
    return () => clearInterval(t);
  }, [load]);

  const setFilter = (f: "all" | "incidents") => {
    const next = new URLSearchParams(searchParams);
    if (f === "all") next.delete("filter");
    else next.set("filter", f);
    setSearchParams(next, { replace: true });
  };

  const boxes = (data?.boxes ?? []).filter(
    (b) => filter === "all" || b.state !== "up",
  );
  const downCaps = (data?.capabilities ?? []).filter((c) => c.state === "down");
  // A runner that is starting / rolling out (admission) is routine, not an
  // incident: it gets its own blue banner, apart from the unhealthy ones.
  const isRollingRunner = (e: FleetEntity) =>
    entityReadiness(e)?.badgeVariant === "info";
  const degradedRunners = (data?.boxes ?? []).flatMap((b) =>
    b.entities
      .filter(
        (e) =>
          e.entity.startsWith("runner:") &&
          e.state !== "up" &&
          !isRollingRunner(e),
      )
      .map((e) => ({ box: b, e })),
  );
  const rollingRunners = (data?.boxes ?? []).flatMap((b) =>
    b.entities.filter(isRollingRunner).map((e) => ({ box: b, e })),
  );
  const macosLines = macosBannerLines(data?.boxes ?? [], nowMs);

  return (
    <div className="max-w-5xl space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Fleet</h1>
          <p className="text-sm text-muted-foreground">
            Every box, every service on it, with the health its own status
            reports — not whether the process is merely alive. A silent
            deploy-agent makes its whole box{" "}
            <span className="font-medium">down</span>. See the{" "}
            <Link
              to="/fleet/flow"
              className="underline-offset-4 hover:underline text-primary"
            >
              Flow view
            </Link>{" "}
            for dependencies; box onboarding lives on{" "}
            <Link
              to="/"
              className="underline-offset-4 hover:underline text-primary"
            >
              Box Approvals
            </Link>
            .
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant={filter === "all" ? "default" : "outline"}
            size="sm"
            onClick={() => setFilter("all")}
          >
            All
          </Button>
          <Button
            variant={filter === "incidents" ? "default" : "outline"}
            size="sm"
            onClick={() => setFilter("incidents")}
          >
            Incidents · {data?.incidents ?? "…"}
          </Button>
          <span className="mx-1 h-4 w-px bg-border" />
          {(["24h", "7d", "30d"] as Window[]).map((w) => (
            <Button
              key={w}
              variant={windowKey === w ? "default" : "outline"}
              size="sm"
              onClick={() => setWindow(w)}
              title="uptime window"
            >
              {w}
            </Button>
          ))}
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              load();
              loadTimeline();
            }}
            disabled={loading}
          >
            {loading ? "refreshing…" : "Refresh"}
          </Button>
        </div>
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}

      {/* Capability banners — the end-to-end "can the platform do X at all"
          view (§4.8). These are what alert; single-instance failures
          below do not. */}
      {downCaps.length > 0 && (
        <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-destructive">
            Platform capability down
          </div>
          {downCaps.map((c) => (
            <div key={c.name} className="mt-1 text-sm">
              <span className="font-mono font-medium">{c.name}</span> —{" "}
              {c.reason}
            </div>
          ))}
        </div>
      )}

      {degradedRunners.length > 0 && (
        <div className="rounded-md border border-yellow-500/40 bg-yellow-500/10 p-3 space-y-2">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-yellow-700 dark:text-yellow-400">
            Build runner{degradedRunners.length > 1 ? "s" : ""} not healthy
          </div>
          {degradedRunners.map(({ box, e }) => (
            <RunnerBanner
              key={`${box.box_id}:${e.entity}`}
              box={box}
              entity={e}
            />
          ))}
        </div>
      )}

      {macosLines.length > 0 && (
        <div
          role="status"
          className="rounded-md border border-yellow-500/40 bg-yellow-500/10 p-3 space-y-1"
        >
          <div className="text-[11px] font-semibold uppercase tracking-wide text-yellow-700 dark:text-yellow-400">
            macOS host not accepting jobs
          </div>
          {macosLines.map((l) => (
            <div key={l} className="text-sm">
              {l}
            </div>
          ))}
          <p className="text-xs text-muted-foreground">
            Publishing is not affected: the macOS arm stays
            &quot;unverified&quot; / &quot;unavailable&quot; until the Mac is
            back.
          </p>
        </div>
      )}

      {rollingRunners.length > 0 && (
        <div className="rounded-md border border-blue-500/40 bg-blue-500/10 p-3 space-y-2">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-blue-700 dark:text-blue-400">
            Build runner{rollingRunners.length > 1 ? "s" : ""} starting /
            rolling out — intake closed until the new pair is fully warm
          </div>
          {rollingRunners.map(({ box, e }) => (
            <RunnerBanner
              key={`${box.box_id}:${e.entity}`}
              box={box}
              entity={e}
            />
          ))}
        </div>
      )}

      {data && (
        <Card>
          <CardHeader>
            <CardTitle>Capabilities</CardTitle>
            <CardDescription>
              Derived across every box.{" "}
              <span className="font-medium">degraded</span> = still working with
              reduced redundancy (not alerted);{" "}
              <span className="font-medium">down</span> = the platform
              can&apos;t do this at all (alerted).
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {data.capabilities.map((c) => (
                <div
                  key={c.name}
                  className="rounded-md border px-3 py-2 text-sm"
                >
                  <div className="flex items-center gap-2">
                    <span
                      className={cn(
                        "h-2.5 w-2.5 rounded-full shrink-0",
                        STATE_DOT[c.state],
                      )}
                    />
                    <span className="font-mono font-medium">{c.name}</span>
                    <span
                      className="text-xs text-muted-foreground truncate"
                      title={c.reason}
                    >
                      {c.reason}
                    </span>
                  </div>
                  {/* Capabilities are recorded as transitions under the
                      synthetic `platform` box by the alerting cron, so
                      they get the same exact uptime strip. */}
                  <UptimeStrip
                    transitions={transitions.filter(
                      (t) =>
                        t.box_id === "platform" &&
                        t.entity === `capability:${c.name}`,
                    )}
                    current={c.state}
                    windowMs={WINDOW_MS[windowKey]}
                    nowMs={nowMs}
                  />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {data && boxes.length === 0 && (
        <p className="text-sm text-muted-foreground">
          {filter === "incidents"
            ? "No incidents — every box is up."
            : "No boxes have connected yet."}
        </p>
      )}

      {boxes.map((b) => {
        const isOpen = expanded.has(b.box_id) || b.state !== "up";
        const sorted = [...b.entities].sort(
          (x, y) =>
            entityLabel(x.entity).order - entityLabel(y.entity).order ||
            x.entity.localeCompare(y.entity),
        );
        const problems = b.entities.filter((e) => e.state !== "up").length;
        return (
          <Card
            key={b.box_id}
            className={cn(
              b.state === "down" && "border-destructive/50",
              b.state === "degraded" && "border-yellow-500/50",
            )}
          >
            <CardHeader>
              <button
                type="button"
                className="w-full text-left"
                onClick={() =>
                  setExpanded((prev) => {
                    const next = new Set(prev);
                    if (next.has(b.box_id)) next.delete(b.box_id);
                    else next.add(b.box_id);
                    return next;
                  })
                }
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <span
                      className={cn(
                        "h-3 w-3 rounded-full shrink-0",
                        STATE_DOT[b.state],
                      )}
                    />
                    <CardTitle className="font-mono truncate">
                      {b.hostname}
                    </CardTitle>
                    <Badge variant={STATE_VARIANT[b.state]}>{b.state}</Badge>
                    {b.kind === "macos" && (
                      <Badge
                        variant="outline"
                        title="Apple-silicon Mac (macos-tester)"
                      >
                        macOS
                      </Badge>
                    )}
                    {b.approval_status && b.approval_status !== "approved" && (
                      <Link
                        to="/"
                        className="text-xs text-primary hover:underline"
                      >
                        {b.approval_status} approval →
                      </Link>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-x-3 text-xs text-muted-foreground">
                    <span title={b.last_seen_at ?? ""}>
                      {b.connected ? "agent heartbeat" : "AGENT SILENT"}{" "}
                      {fmtAgo(b.last_seen_ago_s)}
                    </span>
                    {b.clock_skew_ms !== null &&
                      Math.abs(b.clock_skew_ms) > 30_000 && (
                        <span className="text-yellow-700 dark:text-yellow-400">
                          clock skew {Math.round(b.clock_skew_ms / 1000)}s
                        </span>
                      )}
                    <span>
                      {b.entities.length} entit
                      {b.entities.length === 1 ? "y" : "ies"}
                      {problems > 0 ? ` · ${problems} not up` : ""}
                    </span>
                    <span>{isOpen ? "▾" : "▸"}</span>
                  </div>
                </div>
              </button>
              {!b.connected && (
                <CardDescription className="text-destructive">
                  {b.kind === "macos" ? "macos-tester service" : "deploy-agent"}{" "}
                  has not heartbeated in{" "}
                  {fmtAgo(b.last_seen_ago_s).replace(" ago", "")} — every
                  service on this box is flagged down until it does. Nothing on
                  this box can be observed or driven.
                </CardDescription>
              )}
              {Object.keys(b.installed_components).length > 0 && (
                <CardDescription className="font-mono text-xs">
                  {Object.entries(b.installed_components)
                    .map(([n, v]) => `${n}@${v}`)
                    .join(" · ")}
                </CardDescription>
              )}
            </CardHeader>
            {isOpen && (
              <CardContent>
                {b.kind === "macos" && (
                  <MacosHostPanel call={call} box={b} onChanged={load} />
                )}
                <div className="border rounded-md">
                  {sorted.length === 0 && (
                    <div className="px-3 py-2 text-xs text-muted-foreground">
                      No telemetry from this box yet — the deploy-agent forwards
                      runner/host status once connected.
                    </div>
                  )}
                  {sorted.map((e) => {
                    const { label } = entityLabel(e.entity);
                    const line = entityDetailLine(e);
                    const readiness = entityReadiness(e);
                    return (
                      <div
                        key={e.entity}
                        className="flex items-start justify-between gap-3 px-3 py-2 text-xs border-b last:border-b-0"
                      >
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <span
                              className={cn(
                                "h-2 w-2 rounded-full shrink-0",
                                readiness
                                  ? READINESS_DOT[readiness.badgeVariant]
                                  : STATE_DOT[e.state],
                              )}
                            />
                            <span className="font-mono">{label}</span>
                            {readiness ? (
                              <Badge
                                variant={readiness.badgeVariant}
                                title={`health: ${e.state} · admission: ${readiness.admission}`}
                              >
                                {readiness.badge}
                              </Badge>
                            ) : (
                              <Badge variant={STATE_VARIANT[e.state]}>
                                {e.state}
                              </Badge>
                            )}
                            <span className="text-muted-foreground">
                              {fmtSince(e.since)}
                            </span>
                          </div>
                          <UptimeStrip
                            transitions={transitions.filter(
                              (t) =>
                                t.box_id === b.box_id && t.entity === e.entity,
                            )}
                            current={e.state}
                            windowMs={WINDOW_MS[windowKey]}
                            nowMs={nowMs}
                          />
                          {line &&
                            (e.state !== "up" ||
                              e.entity.startsWith("runner:")) && (
                              <div className="mt-0.5 text-muted-foreground break-words">
                                {line}
                              </div>
                            )}
                          {e.entity.startsWith("runner:") && (
                            <RunnerBuild
                              call={call}
                              detail={
                                (e.detail ?? null) as Record<
                                  string,
                                  unknown
                                > | null
                              }
                            />
                          )}
                          {e.entity.startsWith("unit:") && b.connected && (
                            <DiagnosePanel
                              call={call}
                              boxId={b.box_id}
                              unit={e.entity.slice("unit:".length)}
                            />
                          )}
                          {e.entity.startsWith("runner:") && b.connected && (
                            <RunnerConfigPanel call={call} boxId={b.box_id} />
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            )}
          </Card>
        );
      })}
      <AlertChannelsCard call={call} />
      {data && (
        <p className="text-xs text-muted-foreground">
          generated {new Date(data.generated_at).toLocaleTimeString()} ·
          live-updating
        </p>
      )}
    </div>
  );
}

type Call = <T>(path: string, init?: RequestInit) => Promise<T>;

/**
 * Read-only drill-down (§4.5 / P3): relay `diagnose-unit` to the box's
 * deploy-agent and show what journald / systemctl say — the information
 * that otherwise needs an SSH session. Operator-gated server-side.
 */
function DiagnosePanel({
  call,
  boxId,
  unit,
}: {
  call: Call;
  boxId: string;
  unit: string;
}) {
  const [state, setState] = useState<
    | { kind: "idle" }
    | { kind: "loading" }
    | { kind: "error"; message: string }
    | { kind: "done"; payload: unknown }
  >({ kind: "idle" });
  const run = () => {
    setState({ kind: "loading" });
    call<{ ok: boolean; payload: unknown; reason: string | null }>(
      "/fleet/diagnose",
      {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          box_id: boxId,
          op: "diagnose-unit",
          args: { unit, lines: 120 },
        }),
      },
    )
      .then((r) =>
        r.ok
          ? setState({ kind: "done", payload: r.payload })
          : setState({ kind: "error", message: r.reason ?? "failed" }),
      )
      .catch((err) =>
        setState({ kind: "error", message: errMsg(err, "diagnose failed") }),
      );
  };
  const payload =
    state.kind === "done"
      ? (state.payload as Record<string, unknown> | null)
      : null;
  const journal = payload
    ? (payload["journal"] ?? payload["journal_tail"] ?? payload["logs"] ?? null)
    : null;
  return (
    <div className="mt-1">
      <button
        type="button"
        onClick={run}
        disabled={state.kind === "loading"}
        className="text-primary hover:underline disabled:opacity-50"
      >
        {state.kind === "loading"
          ? "asking the box…"
          : state.kind === "done"
            ? "↻ re-run diagnose"
            : "▶ diagnose (journal + systemctl status)"}
      </button>
      {state.kind === "error" && (
        <span className="ml-2 text-destructive">{state.message}</span>
      )}
      {payload && (
        <pre className="mt-1 max-h-72 overflow-auto whitespace-pre-wrap rounded bg-muted p-2 font-mono text-[10px]">
          {typeof journal === "string"
            ? journal
            : Array.isArray(journal)
              ? journal.join("\n")
              : JSON.stringify(payload, null, 2)}
        </pre>
      )}
      {payload && typeof journal !== "undefined" && journal !== null && (
        <details className="mt-1">
          <summary className="cursor-pointer text-muted-foreground">
            full diagnostic payload
          </summary>
          <pre className="mt-1 max-h-72 overflow-auto whitespace-pre-wrap rounded bg-muted p-2 font-mono text-[10px]">
            {JSON.stringify(payload, null, 2)}
          </pre>
        </details>
      )}
    </div>
  );
}

const CHANNEL_OPTIONS = ["", "latest", "edge"] as const;
const WARMUP_OPTIONS = ["", "on", "auto", "off"] as const;
const inputCls =
  "w-full rounded-md border bg-background px-2 py-1 font-mono text-xs";

/**
 * Runner reconfigure panel — direct operator request, 2026-09-07: "all
 * the pure-deploy configure runner options should be configurable via
 * the management dashboard." Every field mirrors one `configure_runner()`
 * menu option (internal/deploy's runner.sh) 1:1; only fields the operator
 * actually filled in are sent, so leaving something blank never resets
 * it. Relays through POST /fleet/mutate → runner-reconfigure (actions.ts)
 * → `sudo pure-deploy configure runner --flag=value...` on the box.
 */
function RunnerConfigPanel({ call, boxId }: { call: Call; boxId: string }) {
  const [open, setOpen] = useState(false);
  const [maxRunners, setMaxRunners] = useState("");
  const [slotMemMib, setSlotMemMib] = useState("");
  const [coresPerVm, setCoresPerVm] = useState("");
  const [spireHost, setSpireHost] = useState("");
  const [interpChannel, setInterpChannel] = useState<string>("");
  const [compilerChannel, setCompilerChannel] = useState<string>("");
  const [devChannel, setDevChannel] = useState<string>("");
  const [codegenUnits, setCodegenUnits] = useState("");
  const [rustcMaxJobs, setRustcMaxJobs] = useState("");
  const [warmup, setWarmup] = useState<string>("");
  const [state, setState] = useState<
    | { kind: "idle" }
    | { kind: "loading"; label: string }
    | { kind: "error"; message: string }
    | { kind: "done"; label: string }
  >({ kind: "idle" });

  const run = (label: string, args: Record<string, unknown>) => {
    setState({ kind: "loading", label });
    call<{ ok: boolean; reason: string | null }>("/fleet/mutate", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ box_id: boxId, op: "runner-reconfigure", args }),
    })
      .then((r) =>
        r.ok
          ? setState({ kind: "done", label })
          : setState({ kind: "error", message: r.reason ?? "failed" }),
      )
      .catch((err) =>
        setState({ kind: "error", message: errMsg(err, "reconfigure failed") }),
      );
  };

  const applyChanges = () => {
    const args: Record<string, unknown> = {};
    if (maxRunners.trim()) args.max_runners = Number(maxRunners);
    if (slotMemMib.trim()) args.slot_mem_mib = Number(slotMemMib);
    if (coresPerVm.trim()) args.cores_per_vm = Number(coresPerVm);
    if (spireHost.trim()) args.spire_server_host = spireHost.trim();
    if (interpChannel) args.interp_channel = interpChannel;
    if (compilerChannel) args.compiler_channel = compilerChannel;
    if (devChannel) args.dev_channel = devChannel;
    if (codegenUnits.trim()) args.release_codegen_units = Number(codegenUnits);
    if (rustcMaxJobs.trim()) args.rustc_max_jobs = Number(rustcMaxJobs);
    if (warmup) args.warmup = warmup;
    if (Object.keys(args).length === 0) {
      setState({
        kind: "error",
        message: "no fields filled in — nothing to apply",
      });
      return;
    }
    run("apply", args);
  };

  const busy = state.kind === "loading";
  return (
    <div className="mt-1">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="text-primary hover:underline"
      >
        {open ? "▼ hide" : "▶"} configure runner
      </button>
      {open && (
        <div className="mt-1.5 space-y-2 rounded border bg-muted/30 p-2">
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            <label className="space-y-0.5">
              <div className="text-muted-foreground">max runners</div>
              <input
                className={inputCls}
                placeholder="1"
                value={maxRunners}
                onChange={(e) => setMaxRunners(e.target.value)}
              />
            </label>
            <label className="space-y-0.5">
              <div className="text-muted-foreground">slot RAM (MiB)</div>
              <input
                className={inputCls}
                placeholder=">=512"
                value={slotMemMib}
                onChange={(e) => setSlotMemMib(e.target.value)}
              />
            </label>
            <label className="space-y-0.5">
              <div className="text-muted-foreground">physical cores/VM</div>
              <input
                className={inputCls}
                placeholder="e.g. 2"
                value={coresPerVm}
                onChange={(e) => setCoresPerVm(e.target.value)}
              />
            </label>
            <label className="space-y-0.5">
              <div className="text-muted-foreground">release codegen-units</div>
              <input
                className={inputCls}
                placeholder="0 = default"
                value={codegenUnits}
                onChange={(e) => setCodegenUnits(e.target.value)}
              />
            </label>
            <label className="space-y-0.5">
              <div className="text-muted-foreground">rustc max jobs</div>
              <input
                className={inputCls}
                placeholder="0 = auto"
                value={rustcMaxJobs}
                onChange={(e) => setRustcMaxJobs(e.target.value)}
              />
            </label>
            <label className="space-y-0.5">
              <div className="text-muted-foreground">interp channel</div>
              <select
                className={inputCls}
                value={interpChannel}
                onChange={(e) => setInterpChannel(e.target.value)}
              >
                {CHANNEL_OPTIONS.map((c) => (
                  <option key={c} value={c}>
                    {c || "(unchanged)"}
                  </option>
                ))}
              </select>
            </label>
            <label className="space-y-0.5">
              <div className="text-muted-foreground">compiler channel</div>
              <select
                className={inputCls}
                value={compilerChannel}
                onChange={(e) => setCompilerChannel(e.target.value)}
              >
                {CHANNEL_OPTIONS.map((c) => (
                  <option key={c} value={c}>
                    {c || "(unchanged)"}
                  </option>
                ))}
              </select>
            </label>
            <label className="space-y-0.5">
              <div className="text-muted-foreground">both channels (dev)</div>
              <select
                className={inputCls}
                value={devChannel}
                onChange={(e) => setDevChannel(e.target.value)}
              >
                {CHANNEL_OPTIONS.map((c) => (
                  <option key={c} value={c}>
                    {c || "(unchanged)"}
                  </option>
                ))}
              </select>
            </label>
            <label className="space-y-0.5">
              <div className="text-muted-foreground">
                warm-up (on = jobs wait for prewarm, auto = prewarm in idle
                time, jobs first; off = cold builds)
              </div>
              <select
                className={inputCls}
                value={warmup}
                onChange={(e) => setWarmup(e.target.value)}
              >
                {WARMUP_OPTIONS.map((c) => (
                  <option key={c} value={c}>
                    {c || "(unchanged)"}
                  </option>
                ))}
              </select>
            </label>
            <label className="space-y-0.5">
              <div className="text-muted-foreground">SPIRE server host</div>
              <input
                className={inputCls}
                placeholder="pure.dev"
                value={spireHost}
                onChange={(e) => setSpireHost(e.target.value)}
              />
            </label>
          </div>
          <div className="flex flex-wrap items-center gap-2 pt-1">
            <Button size="sm" disabled={busy} onClick={applyChanges}>
              {busy && state.label === "apply" ? "applying…" : "Apply changes"}
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={busy}
              onClick={() => run("reapply", { reapply: true })}
            >
              {busy && state.label === "reapply"
                ? "re-rendering…"
                : "Re-render env + restart"}
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={busy}
              onClick={() => run("spire", { request_spire_entry: true })}
            >
              {busy && state.label === "spire"
                ? "requesting…"
                : "Request SPIRE entry"}
            </Button>
            {state.kind === "error" && (
              <span className="text-destructive">{state.message}</span>
            )}
            {state.kind === "done" && (
              <span className="text-green-600">✓ applied</span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Alert channels (§4.8): where a capability `down` (and its recovery)
 * goes. Deliberately no severity knob — by owner decision only "the
 * platform cannot X" pages anyone. Anyone can see the list; add / delete /
 * test are operator-gated server-side.
 */
interface AlertChannel {
  id: string;
  kind: "email" | "webhook";
  target: string;
  enabled: number;
  created_by: string;
  created_at: string;
  has_secret: number;
  last_sent_at: string | null;
  last_error: string | null;
}
function AlertChannelsCard({ call }: { call: Call }) {
  const [channels, setChannels] = useState<AlertChannel[] | null>(null);
  const [emailConfigured, setEmailConfigured] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [kind, setKind] = useState<"email" | "webhook">("webhook");
  const [target, setTarget] = useState("");
  const [secret, setSecret] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<Record<string, string>>({});

  const load = useCallback(() => {
    call<{ ok: true; channels: AlertChannel[]; email_configured: boolean }>(
      "/fleet/alert-channels",
    )
      .then((r) => {
        setChannels(r.channels);
        setEmailConfigured(r.email_configured);
        setError(null);
      })
      .catch((err) => setError(errMsg(err, "failed to load alert channels")));
  }, [call]);
  useEffect(load, [load]);

  const add = () => {
    setBusy("add");
    call<{ ok: boolean; error?: string }>("/fleet/alert-channels", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ kind, target, secret: secret || undefined }),
    })
      .then(() => {
        setTarget("");
        setSecret("");
        load();
      })
      .catch((err) => setError(errMsg(err, "failed to add channel")))
      .finally(() => setBusy(null));
  };
  const remove = (id: string) => {
    setBusy(id);
    call(`/fleet/alert-channels/${encodeURIComponent(id)}`, {
      method: "DELETE",
    })
      .then(load)
      .catch((err) => setError(errMsg(err, "failed to delete channel")))
      .finally(() => setBusy(null));
  };
  const test = (id: string) => {
    setBusy(id);
    call<{ ok: boolean; error?: string }>("/fleet/alert-channels/test", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ id }),
    })
      .then((r) =>
        setTestResult((p) => ({
          ...p,
          [id]: r.ok ? "test sent ✓" : (r.error ?? "failed"),
        })),
      )
      .catch((err) =>
        setTestResult((p) => ({ ...p, [id]: errMsg(err, "test failed") })),
      )
      .finally(() => setBusy(null));
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Alert channels</CardTitle>
        <CardDescription>
          Notified only when a platform capability goes{" "}
          <span className="font-medium">down</span> (and when it recovers),
          after persisting for 60 s. One box of several failing is an incident
          on this page, not a notification.
          {!emailConfigured && (
            <span className="block mt-1 text-yellow-700 dark:text-yellow-400">
              Email delivery is not configured on pure-auth-broker
              (RESEND_API_KEY / ALERT_EMAIL_FROM) — email channels will log a
              delivery failure until it is. Webhooks work regardless.
            </span>
          )}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {error && <p className="text-sm text-destructive">{error}</p>}
        {channels && channels.length === 0 && (
          <p className="text-sm text-muted-foreground">
            No channels yet — a capability outage is currently visible only
            here.
          </p>
        )}
        {channels && channels.length > 0 && (
          <div className="border rounded-md">
            {channels.map((c) => (
              <div
                key={c.id}
                className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-xs border-b last:border-b-0"
              >
                <div className="min-w-0">
                  <Badge variant="secondary">{c.kind}</Badge>{" "}
                  <span className="font-mono break-all">{c.target}</span>
                  {c.has_secret ? (
                    <span className="ml-2 text-muted-foreground">signed</span>
                  ) : null}
                  <div className="text-muted-foreground">
                    added by {c.created_by}
                    {c.last_sent_at
                      ? ` · last sent ${c.last_sent_at} UTC`
                      : " · never sent"}
                    {c.last_error ? (
                      <span className="text-destructive">
                        {" "}
                        · last error: {c.last_error}
                      </span>
                    ) : null}
                    {testResult[c.id] ? ` · ${testResult[c.id]}` : ""}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={busy === c.id}
                    onClick={() => test(c.id)}
                  >
                    Send test
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={busy === c.id}
                    onClick={() => remove(c.id)}
                  >
                    Remove
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <select
            className="rounded-md border bg-background px-2 py-1 text-sm"
            value={kind}
            onChange={(e) => setKind(e.target.value as "email" | "webhook")}
          >
            <option value="webhook">webhook</option>
            <option value="email">email</option>
          </select>
          <input
            className="min-w-[16rem] flex-1 rounded-md border bg-background px-2 py-1 font-mono text-sm"
            placeholder={
              kind === "email"
                ? "ops@example.com"
                : "https://hooks.slack.com/services/…"
            }
            value={target}
            onChange={(e) => setTarget(e.target.value)}
          />
          {kind === "webhook" && (
            <input
              className="w-40 rounded-md border bg-background px-2 py-1 font-mono text-sm"
              placeholder="HMAC secret (optional)"
              value={secret}
              onChange={(e) => setSecret(e.target.value)}
            />
          )}
          <Button size="sm" disabled={!target || busy === "add"} onClick={add}>
            Add
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * The runner's own root cause, rendered straight from its status payload
 * (fleet-health-dashboard-plan.md §4.5) — hint first and largest, then
 * code + summary, then click-to-expand stderr. This is the exact
 * information that lived only in journald during the 2026-09-04 incident.
 */
function RunnerBanner({ box, entity }: { box: FleetBox; entity: FleetEntity }) {
  const [showStderr, setShowStderr] = useState(false);
  const runnerId = entity.entity.slice("runner:".length);
  const payload = (box.telemetry[`runner-status:${runnerId}`] ??
    null) as Record<string, unknown> | null;
  const compiler = (payload?.["compiler"] ?? {}) as Record<string, unknown>;
  const interp = (payload?.["interp"] ?? {}) as Record<string, unknown>;
  const err = (compiler["error"] ?? null) as Record<string, unknown> | null;
  const status = String(payload?.["status"] ?? entity.state);
  const readiness = entityReadiness(entity);
  const lastOk = compiler["lastOkTs"]
    ? `last OK ${new Date(Number(compiler["lastOkTs"])).toLocaleString()}`
    : "no successful prewarm yet";
  const nextRetry = compiler["nextRetryTs"]
    ? `next retry ${new Date(Number(compiler["nextRetryTs"])).toLocaleTimeString()}`
    : "";
  const versionLines = [
    packageVersionLine("compiler", compiler),
    packageVersionLine("interp", interp),
  ].filter((s): s is string => s !== null);
  return (
    <div
      className={cn(
        "rounded-md border bg-card p-2 text-xs",
        readiness?.badgeVariant === "info"
          ? "border-blue-500/40"
          : "border-yellow-500/40",
      )}
    >
      <div className="flex flex-wrap items-baseline gap-2">
        <span className="font-mono font-medium">{runnerId}</span>
        <span className="text-muted-foreground">on {box.hostname}</span>
        {readiness ? (
          <Badge
            variant={readiness.badgeVariant}
            title={`health: ${entity.state} · status: ${status}`}
          >
            {readiness.badge}
          </Badge>
        ) : (
          <Badge variant={STATE_VARIANT[entity.state]}>{status}</Badge>
        )}
        {versionLines.length > 0 && (
          <span className="font-mono text-muted-foreground">
            {versionLines.join(" · ")}
          </span>
        )}
        <span className="text-muted-foreground">
          attempts {String(compiler["attempts"] ?? "?")} · {lastOk}
          {nextRetry ? ` · ${nextRetry}` : ""}
        </span>
      </div>
      {readiness &&
        (readiness.admission !== "serving" || readiness.backoff) && (
          <RunnerAdmissionDetail readiness={readiness} />
        )}
      {err ? (
        <>
          <div className="mt-1 font-medium">
            <span className="mr-2 inline-block rounded bg-yellow-500/20 px-1.5 py-0.5 font-mono text-[10px] uppercase">
              {String(err["code"] ?? "")}
            </span>
            {String(err["hint"] ?? "")}
          </div>
          <div className="mt-0.5 text-muted-foreground">
            {String(err["summary"] ?? "")}
          </div>
          {typeof err["stderr"] === "string" && err["stderr"] && (
            <>
              <button
                type="button"
                onClick={() => setShowStderr((v) => !v)}
                className="mt-1 text-primary hover:underline"
              >
                {showStderr ? "▼ hide" : "▶ show"} stderr tail
              </button>
              {showStderr && (
                <pre className="mt-1 max-h-48 overflow-auto whitespace-pre-wrap rounded bg-muted p-2 font-mono text-[10px]">
                  {err["stderr"]}
                </pre>
              )}
            </>
          )}
        </>
      ) : readiness && readiness.admission !== "serving" ? null : (
        // RunnerAdmissionDetail above already carries the reason/progress.
        <div className="mt-1 text-muted-foreground">
          {entityDetailLine(entity) ?? "no detail reported"}
        </div>
      )}
    </div>
  );
}

/** Why intake is closed + the running rollout's progress (phase, target
 *  version, per-bucket warm state and last failure) — runner `admission` /
 *  `rollout` / `warmTargetSets` / `rolloutBackoff`. Also shown while serving
 *  when a staged rollout failed and is backing off (2026-10-07). */
function RunnerAdmissionDetail({ readiness }: { readiness: RunnerReadiness }) {
  const r = readiness.rollout;
  const buckets = r?.buckets?.buckets.length
    ? r.buckets.buckets
    : (readiness.staged?.buckets ?? []);
  const stagedVersion = r?.targetVersion ?? readiness.staged?.version ?? null;
  return (
    <div className="mt-1 space-y-0.5">
      {readiness.reason && (
        <div>
          <span className="text-muted-foreground">
            {readiness.admission === "degraded"
              ? "degraded: "
              : "intake closed: "}
          </span>
          {readiness.reason}
        </div>
      )}
      {readiness.progress && (
        <div>
          <span className="text-muted-foreground">rollout: </span>
          {readiness.progress}
        </div>
      )}
      {readiness.backoffText && (
        <div className="text-yellow-700 dark:text-yellow-400">
          <span className="text-muted-foreground">backoff: </span>
          {readiness.backoffText}
        </div>
      )}
      {buckets.length > 0 && (
        <div>
          {stagedVersion && (
            <span className="text-muted-foreground">
              warm targets of v{stagedVersion}:
            </span>
          )}
          <WarmBucketList buckets={buckets} />
        </div>
      )}
    </div>
  );
}
